import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import apiService, { TWRChartData } from '../services/api';
import '../styles/bloomberg-theme.css';

interface TWRChartProps {
  period?: string;
  userId?: string;
  isConsolidated?: boolean;
  height?: number;
  setPeriod?: (period: string) => void;
}

interface ChartDataPoint {
  timestamp: string;
  time: string;
  date: string;
  daily: number;
  weekly: number;
  monthly: number;
  total: number;
}

const TWRChartBloomberg: React.FC<TWRChartProps> = ({
  setPeriod,
  period,
  userId,
  isConsolidated = false,
  height = 400
}) => {
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibleLines, setVisibleLines] = useState({
    daily: false,
    weekly: false,
    monthly: false,
    total: true
  });

  const fetchChartData = useCallback(async () => {
    try {
      setLoading(true);

      let data: TWRChartData;
      if (isConsolidated) {
        data = await apiService.getConsolidatedTWRChart(period);
      } else if (userId) {
        data = await apiService.getAdminUserTWRChart(userId, period);
      } else {
        data = await apiService.getTWRChart(period);
      }

      // Transform data for chart display - handle empty/undefined data
      if (!data || !data.timestamps || data.timestamps.length === 0) {
        setChartData([]);
        setError(null);
        return;
      }

      const formattedData: ChartDataPoint[] = data.timestamps.map((timestamp: string, index: number) => {
        const date = new Date(timestamp);

        // Format X-axis label based on period
        let dateLabel: string;
        if (period === '24h') {
          // For 24h, show hour:minute
          dateLabel = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        } else if (period === '7d') {
          // For 7d, show day and month
          dateLabel = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          });
        } else {
          // For 30d, mtd, ytd and all, show month/day
          dateLabel = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          });
        }

        return {
          timestamp,
          time: date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }),
          date: dateLabel,
          daily: data.twr_daily[index] || 0,
          weekly: data.twr_weekly[index] || 0,
          monthly: data.twr_monthly[index] || 0,
          total: data.twr_total[index] || 0
        };
      });

      setChartData(formattedData);
      setError(null);
    } catch (err) {
      console.error('Failed to load TWR chart data:', err);
      setError('Failed to load TWR chart data');
    } finally {
      setLoading(false);
    }
  }, [period, userId, isConsolidated]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'var(--bb-bg-secondary)',
          border: '1px solid var(--bb-amber)',
          padding: '12px',
          fontFamily: 'var(--bb-font-mono)',
          fontSize: '11px'
        }}>
          <div style={{ color: 'var(--bb-amber)', marginBottom: '8px', fontWeight: 'bold' }}>
            {label}
          </div>
          {payload.map((entry: any, index: number) => {
            if (!visibleLines[entry.dataKey as keyof typeof visibleLines]) return null;
            const isPositive = entry.value >= 0;
            return (
              <div key={`item-${index}`} style={{ marginBottom: '4px' }}>
                <span style={{ color: entry.color }}>{entry.name}: </span>
                <span className={isPositive ? 'bb-value-positive' : 'bb-value-negative'}>
                  {formatPercent(entry.value)}
                </span>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const toggleLine = (dataKey: keyof typeof visibleLines) => {
    setVisibleLines(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }));
  };

  const CustomLegend = () => {
    const legendItems = [
      { key: 'total', label: 'TOTAL TWR', color: '#FF6B6B' }
    ];

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '20px',
        marginTop: '10px',
        fontSize: '11px',
        fontFamily: 'var(--bb-font-mono)'
      }}>
        {legendItems.map(item => (
          <div
            key={item.key}
            onClick={() => toggleLine(item.key as keyof typeof visibleLines)}
            style={{
              cursor: 'pointer',
              opacity: visibleLines[item.key as keyof typeof visibleLines] ? 1 : 0.4,
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            <div style={{
              width: '12px',
              height: '2px',
              backgroundColor: item.color
            }} />
            <span style={{ color: 'var(--bb-text-secondary)' }}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <span className="bb-blink" style={{ color: 'var(--bb-amber)', fontSize: '12px' }}>LOADING TWR CHART DATA...</span>
      </div>
    );
  }

  if (error || chartData.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', backgroundColor: 'var(--bb-bg-secondary)', border: '1px solid var(--bb-border)' }}>
        <span style={{ color: 'var(--bb-red)', fontSize: '12px' }}>{error || 'NO TWR DATA AVAILABLE'}</span>
      </div>
    );
  }

  // Calculate domain for Y-axis with reasonable bounds
  const allValues = chartData.flatMap(d => [d.daily, d.weekly, d.monthly, d.total])
    .filter(v => v !== null && v !== undefined && !isNaN(v) && isFinite(v));

  let minValue = Math.min(...allValues, 0);
  let maxValue = Math.max(...allValues, 0);

  // Apply reasonable bounds to prevent extreme scales
  minValue = Math.max(minValue, -20);
  maxValue = Math.min(maxValue, 20);

  // Ensure we have at least a small range
  if (Math.abs(maxValue - minValue) < 1) {
    minValue = Math.min(minValue, -2);
    maxValue = Math.max(maxValue, 2);
  }

  const padding = Math.abs(maxValue - minValue) * 0.1;

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      {/* Period Selector */}
      <div style={{
        display: 'flex',
        gap: '5px',
        marginBottom: '10px',
        justifyContent: 'flex-end'
      }}>
        {['24h', '7d', 'mtd', '30d', 'ytd', 'all'].map(p => (
          <button
            key={p}
            className="hidden"
            onClick={() => setPeriod?.(p)}
            style={{
              padding: '4px 8px',
              backgroundColor: period === p ? 'var(--bb-amber)' : 'var(--bb-bg-secondary)',
              color: period === p ? 'var(--bb-black)' : 'var(--bb-amber)',
              border: '1px solid var(--bb-amber)',
              fontFamily: 'var(--bb-font-mono)',
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {p === 'all' ? 'ALL' : p === 'mtd' ? 'MTD' : p === 'ytd' ? 'YTD' : p.toUpperCase()}
          </button>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={height - 40}>
        <LineChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 10, bottom: 30 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--bb-dark-gray)"
            vertical={false}
          />

          <XAxis
            dataKey="date"
            stroke="var(--bb-gray)"
            tick={{ fill: 'var(--bb-gray)', fontSize: 10 }}
            axisLine={{ stroke: 'var(--bb-gray)' }}
            interval={period === '24h' ? 'preserveStartEnd' : period === '7d' ? 0 : 'preserveStartEnd'}
          />

          <YAxis
            stroke="var(--bb-gray)"
            tick={{ fill: 'var(--bb-gray)', fontSize: 10 }}
            axisLine={{ stroke: 'var(--bb-gray)' }}
            domain={[minValue - padding, maxValue + padding]}
            tickFormatter={(value) => `${value.toFixed(1)}%`}
          />

          <Tooltip content={<CustomTooltip />} />

          {visibleLines.daily && (
            <Line
              type="monotone"
              dataKey="daily"
              name="Daily TWR"
              stroke="#00FF00"
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 3, fill: '#00FF00' }}
            />
          )}

          {visibleLines.weekly && (
            <Line
              type="monotone"
              dataKey="weekly"
              name="Weekly TWR"
              stroke="#FFD700"
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 3, fill: '#FFD700' }}
            />
          )}

          {visibleLines.monthly && (
            <Line
              type="monotone"
              dataKey="monthly"
              name="Monthly TWR"
              stroke="#00CED1"
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 3, fill: '#00CED1' }}
            />
          )}

          {visibleLines.total && (
            <Line
              type="monotone"
              dataKey="total"
              name="Total TWR"
              stroke="#FF6B6B"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 3, fill: '#FF6B6B' }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
      <CustomLegend />
    </div>
  );
};

export default TWRChartBloomberg;